import { type Documentation, type DocumentationConfigs, documentationSchema } from '@si/types';
import { post } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { queryKeys } from './queryKeys';

interface Params {
  projectId: string;
  configs: DocumentationConfigs;
  filename: string;
}

export function useCreateReportDraft() {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, error, isPending, isSuccess, reset } = useMutation<
    Documentation,
    Error,
    Params
  >({
    mutationFn: ({ projectId, configs, filename }) =>
      post({
        url: `/projects/${projectId}/reports/drafts`,
        body: { configs, filename },
        schema: documentationSchema,
      }),
    onSuccess: (_, { projectId }) =>
      queryClient.invalidateQueries({ queryKey: queryKeys.getReports(projectId) }),
  });

  const createReportDraft = useCallback((params: Params) => mutate(params), [mutate]);
  const createReportDraftAsync = useCallback((params: Params) => mutateAsync(params), [mutateAsync]);

  return {
    createReportDraft,
    createReportDraftAsync,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
